import { CACHE_EXPIRATION_MS } from '../common/constants/services';

interface CacheItem<T> {
  value: T;
  expiry: number;
}

class CacheService {
  static async set<T>(key: string, value: T, expiresIn: number = CACHE_EXPIRATION_MS): Promise<void> {
    const expiry = Date.now() + expiresIn * 1000;
    const item: CacheItem<T> = { value, expiry };
    localStorage.setItem(key, JSON.stringify(item));
  }

  static async get<T>(key: string, defaultValue: T | (() => Promise<T> | T), expiresIn: number = CACHE_EXPIRATION_MS): Promise<T> {
    const cachedItem = localStorage.getItem(key);

    if (cachedItem) {
      const item: CacheItem<T> = JSON.parse(cachedItem);
      const itemIsNotExpired = Date.now() < item.expiry;
      if (itemIsNotExpired) {
        return item.value;
      } else {
        localStorage.removeItem(key);
      }
    }

    // If no valid cache exists, resolve the default value
    const value = typeof defaultValue === 'function' ? await (defaultValue as () => Promise<T>)() : defaultValue;
    // Store the resolved value in cache
    await this.set(key, value, expiresIn);
    return value;
  }

  static remove(key: string): void {
    localStorage.removeItem(key);
  }

  static clearAll(): void {
    localStorage.clear();
  }
}

export default CacheService;
